import Big from 'big.js';
import * as lineitems from 'smg/customer-api/lineitems';
import * as html from 'smg/html';
import { DollarFormat } from 'smg/util';
import Batch from 'smg/batch';
import * as hyejoo from 'smg/customer-api/hyejoo';
import * as models from 'smg/models';

document.querySelectorAll('.pricing-table').forEach(async $pricingTable => {
    const lineItem = $pricingTable.getAttribute('data-line-item');
    const type = $pricingTable.getAttribute('data-type');
    const minQty = parseInt($pricingTable.getAttribute('data-min-qty') || '0', 10);
    const allowedSizes = new Set(($pricingTable.getAttribute('data-sizes') ?? '').split(';').filter(Boolean));

    if (!lineItem || !type) {
        return;
    }

    const $thead = $pricingTable.querySelector('thead') as HTMLTableSectionElement;
    const $tbody = $pricingTable.querySelector('tbody') as HTMLTableSectionElement;

    $thead.innerHTML = '';
    $tbody.innerHTML = '';

    const sizes = await lineitems.GetSizes(lineItem, type);

    const qtys = new Set<number>();
    for (const size of sizes) {
        if (allowedSizes.size && !allowedSizes.has(size.name)) {
            continue;
        }

        for (const price of size.prices) {
            if (minQty && price.quantity < minQty) {
                continue;
            }

            qtys.add(price.quantity);
        }
    }

    const qtysSorted = Array.from(qtys).sort((a, b) => a - b);

    const pricesBySize = new Map<string, Map<number, Big>>();
    for (const size of sizes) {
        if (allowedSizes.size && !allowedSizes.has(size.name)) {
            continue;
        }

        const prices = new Map<number, Big>();
        for (const price of size.prices) {
            prices.set(price.quantity, price.price);
        }
        pricesBySize.set(size.name, prices);
    }

    const $tr = html.FromString('<tr><th>Size</th></tr>');
    $thead.appendChild($tr);

    for (const qty of qtys) {
        $tr.appendChild(html.FromString(`<th>${Intl.NumberFormat().format(qty)}</th>`));
    }

    for (const [size, prices] of pricesBySize) {
        const $tr = html.FromString(`<tr><th scope="row">${html.Escape(size)}</th></tr>`);
        $tbody.appendChild($tr);

        for (const qty of qtysSorted) {
            const price = prices.get(qty);
            $tr.appendChild(html.FromString(`<td>${price ? DollarFormat(price) : ''}</td>`));
        }
    }
});

document.querySelectorAll('.pricing-table-mold-fees').forEach(async $pricingTable => {
    const lineItemName = $pricingTable.getAttribute('data-line-item');
    const type = $pricingTable.getAttribute('data-type');
    const overQty = parseInt($pricingTable.getAttribute('data-over-qty') || '0', 10);
    const twoSides = $pricingTable.getAttribute('data-two-sides') === 'true';
    const allowedSizes = new Set(($pricingTable.getAttribute('data-sizes') ?? '').split(';').filter(Boolean));

    if (!lineItemName || !type) {
        return;
    }

    const $thead = $pricingTable.querySelector('thead') as HTMLTableSectionElement;
    const $tbody = $pricingTable.querySelector('tbody') as HTMLTableSectionElement;

    $thead.innerHTML = '';
    $tbody.innerHTML = '';

    const $headerTR = html.FromString('<tr><th>Size</th></tr>');
    if (twoSides) {
        $headerTR.appendChild(html.FromString('<th>One Side</th>'));
        $headerTR.appendChild(html.FromString('<th>Two Sides</th>'));
    }

    const moldFeesBatch = new Batch();

    await lineitems.GetTypes(lineItemName).then(async lit => {
        const lineItemType = lit.find(item => item.name === type);
        if (overQty) {
            $headerTR.appendChild(html.FromString(`<th>Order ${overQty}+</th>`));
        }

        $thead.appendChild($headerTR);

        const sizes = lineItemType?.sizes.filter(size => {
            if (!allowedSizes.size || allowedSizes.has(size.name)) {
                return true;
            }
            return false;
        });

        if (!sizes) {
            return;
        }

        interface sizeMoldPrices {
            oneSide: Big;
            twoSides: Big;
            overQtyFree: boolean;
        }

        const sizeMoldPrices = new Map<string, sizeMoldPrices>();

        for (const size of sizes) {
            const prices: sizeMoldPrices = {
                oneSide: Big(0),
                twoSides: Big(0),
                overQtyFree: true,
            };

            void moldFeesBatch.get<hyejoo.ChildLineItems>('/child-line-items', {
                params: {
                    'q': `lineItem:"${lineItemName}" mold:yes`,
                    'data': 'lineItemID,networkChildLineItem,childLineItemOptions,name,pluralName',
                    'width': size.width,
                    'height': size.height,
                    'quantity': overQty ? overQty - 1 : 1,
                    'lineItemTypeID': lineItemType?.id,
                    'lineItemTypeSizeID': size.id,
                },
            }).then(resp => {
                for (const childLineItem of resp.data.childLineItems) {
                    if (childLineItem.id === models.ChildLineItemID.CHILD_LINE_ITEM_PIN_MOLD_FEE) {
                        prices.oneSide = Big(childLineItem.networkChildLineItem?.price || '0');
                    }

                    if (childLineItem.childLineItemOptions) {
                        for (const childLineItemOption of childLineItem.childLineItemOptions) {
                            if (childLineItemOption.id === models.ChildLineItemOptionID.CHILD_LINE_ITEM_OPTION_COIN_MOLD_FEE_NEW_ONE_SIDE) {
                                prices.oneSide = Big(childLineItemOption.networkChildLineItemOption?.price || '0');
                            }
                            if (childLineItemOption.id === models.ChildLineItemOptionID.CHILD_LINE_ITEM_OPTION_COIN_MOLD_FEE_NEW_BOTH_SIDES || childLineItemOption.id === models.ChildLineItemOptionID.CHILD_LINE_ITEM_OPTION_MEDAL_MOLD_FEE_NEW_BOTH_SIDES) {
                                prices.twoSides = Big(childLineItemOption.networkChildLineItemOption?.price || '0');
                            }
                        }
                    }
                }
            });
            void moldFeesBatch.get<hyejoo.ChildLineItems>('/child-line-items', {
                params: {
                    'q': `lineItem:"${lineItemName}" mold:yes`,
                    'data': 'lineItemID,networkChildLineItem,childLineItemOptions,name,pluralName',
                    'width': size.width,
                    'height': size.height,
                    'quantity': overQty + 1,
                    'lineItemTypeID': lineItemType?.id,
                    'lineItemTypeSizeID': size.id,
                },
            }).then(resp => {
                for (const childLineItem of resp.data.childLineItems) {
                    if (childLineItem.id === models.ChildLineItemID.CHILD_LINE_ITEM_PIN_MOLD_FEE) {
                        prices.overQtyFree = !Big(childLineItem.networkChildLineItem?.price || '0').gt(0);
                    }

                    if (childLineItem.childLineItemOptions) {
                        for (const childLineItemOption of childLineItem.childLineItemOptions) {
                            if (childLineItemOption.id === models.ChildLineItemOptionID.CHILD_LINE_ITEM_OPTION_COIN_MOLD_FEE_NEW_ONE_SIDE) {
                                prices.overQtyFree = !Big(childLineItemOption.networkChildLineItemOption?.price || '0').gt(0);
                            }
                            if (childLineItemOption.id === models.ChildLineItemOptionID.CHILD_LINE_ITEM_OPTION_COIN_MOLD_FEE_NEW_BOTH_SIDES || childLineItemOption.id === models.ChildLineItemOptionID.CHILD_LINE_ITEM_OPTION_MEDAL_MOLD_FEE_NEW_BOTH_SIDES) {
                                prices.overQtyFree = !Big(childLineItemOption.networkChildLineItemOption?.price || '0').gt(0);
                            }
                        }
                    }
                }
            });

            sizeMoldPrices.set(size.name, prices);
        }
        await moldFeesBatch.Exec().then(() => {
            for (const size of sizes) {
                const prices = sizeMoldPrices.get(size.name);
                if (!prices) {
                    continue;
                }
                const $tr = html.FromString(`<tr><th scope="row">${html.Escape(size.name)}</th></tr>`);
                $tr.appendChild(html.FromString(`<td>${prices.oneSide ? DollarFormat(prices.oneSide) : ''}</td>`));
                if (twoSides) {
                    $tr.appendChild(html.FromString(`<td>${prices.twoSides ? DollarFormat(prices.twoSides) : ''}</td>`));
                }
                if (overQty) {
                    $tr.appendChild(html.FromString(`<td>${prices.overQtyFree ? '<b>FREE</b>' : '-'}</td>`));
                }
                $tbody.appendChild($tr);
            }
        });
    });
});
